import { Link, NavLink, Outlet } from "react-router-dom";
import { Phone, Mail, MapPin, Menu, X } from "lucide-react";
import { useState } from "react";
import { Button } from "../components/ui/button";
import NewsletterFooterForm from "./NewsletterFooterForm";

const LOGO_LIGHT = "https://customer-assets-gfyr7b9c.emergentagent.net/job_services-electro-dao/artifacts/y1v03j5w_Logo%20Couleur%20Fond%20Clair_PNG%20HD_Fond%20Transparent.webp";
const LOGO_DARK = "https://customer-assets-gfyr7b9c.emergentagent.net/job_services-electro-dao/artifacts/9br5srva_Logo%20Couleur%20Fond%20Fonc%C3%A9_PNG%20HD_Fond%20Transparent.webp";

const nav = [
  { to: "/", label: "Accueil" },
  { to: "/electrotechnique", label: "Électrotechnique" },
  { to: "/irve", label: "IRVE" },
  { to: "/location", label: "Location matériel" },
  { to: "/dao", label: "DAO" },
  { to: "/a-propos", label: "À propos" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-24 lg:h-32 flex items-center justify-between">
          <Link to="/" className="flex items-center group" data-testid="navbar-logo">
            <img src={LOGO_LIGHT} alt="INGélec — Électrotechnique, maintenance & location de matériel" className="h-20 lg:h-28 w-auto object-contain" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive ? "text-[#1B3A6B] bg-[#1B3A6B]/5" : "text-slate-600 hover:text-[#1B3A6B] hover:bg-slate-50"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href="tel:0692371331" className="text-sm text-slate-600 hover:text-[#1B3A6B] flex items-center gap-1.5" data-testid="navbar-phone">
              <Phone className="w-4 h-4" /> 0692 37 13 31
            </a>
            <Link to="/contact">
              <Button className="bg-[#1B3A6B] hover:bg-[#142c54] text-white rounded-full px-5" data-testid="navbar-cta-contact">Demander un devis</Button>
            </Link>
          </div>

          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="navbar-mobile-toggle" aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-1">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `block px-3 py-2 rounded-md text-sm font-medium ${isActive ? "bg-[#1B3A6B] text-white" : "text-slate-700 hover:bg-slate-100"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/contact" onClick={() => setOpen(false)} className="block">
              <Button className="w-full bg-[#FBBF24] hover:bg-[#F59E0B] text-[#0F172A] font-semibold mt-2" data-testid="mobile-cta-contact">Demander un devis</Button>
            </Link>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-[#0F172A] text-slate-300 mt-24" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-5 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <img src={LOGO_DARK} alt="INGélec — Électrotechnique, maintenance & location de matériel" className="h-32 lg:h-40 w-auto object-contain" />
            </div>
            <p className="text-sm leading-relaxed text-slate-400 max-w-md">
              Prestations électrotechniques, location de matériel BTP et DAO sur toute l'Île de La Réunion.
              Installateur qualifié IRVE niveau 1 & 2 – Qualifelec.
            </p>
          </div>
          <div>
            <div className="text-white font-semibold mb-3">Activités</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/electrotechnique" className="hover:text-white">Électrotechnique</Link></li>
              <li><Link to="/irve" className="hover:text-white">Bornes IRVE</Link></li>
              <li><Link to="/location" className="hover:text-white">Location matériel</Link></li>
              <li><Link to="/dao" className="hover:text-white">Plans & DAO</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-white font-semibold mb-3">Contact</div>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 text-[#FBBF24]" /> 0692 37 13 31</li>
              <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 text-[#FBBF24]" /> contact@ingelec.re</li>
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#FBBF24]" /> Sainte-Suzanne, La Réunion</li>
            </ul>
          </div>
          <div>
            <NewsletterFooterForm />
          </div>
        </div>
        <div className="border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500">
            <div>© {new Date().getFullYear()} INGélec — Tous droits réservés.</div>
            <div className="flex gap-4 mt-2 sm:mt-0">
              <Link to="/a-propos" className="hover:text-white">À propos</Link>
              <Link to="/contact" className="hover:text-white">Contact</Link>
              <Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link>
              <Link to="/desinscription" className="hover:text-white">Désinscription</Link>
              <Link to="/admin/login" className="hover:text-white">Admin</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
